import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { X, Star } from 'lucide-react';
import { VITRINE_DEFAULTS } from '../constants/vitrineDefaults';

const DEFAULTS = VITRINE_DEFAULTS;

const Home = () => {
  const [searchParams] = useSearchParams();
  const tableQR = searchParams.get('table');
  const [v, setV] = useState(DEFAULTS);
  const [gallery, setGallery] = useState([]);
  const [showReservation, setShowReservation] = useState(false);
  const [resvForm, setResvForm] = useState({ nom: '', prenom: '', telephone: '', email: '', couverts: 2, date: '', heure: '19:00', message: '' });
  const [resvLoading, setResvLoading] = useState(false);
  const [resvSuccess, setResvSuccess] = useState(false);
  const [resvError, setResvError] = useState('');
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0 });

  useEffect(() => {
    api.get('/vitrine').then(res => setV({ ...DEFAULTS, ...res.data })).catch(() => {});
    api.get('/vitrine/gallery').then(res => setGallery(res.data)).catch(() => {});
    api.get('/reviews?limit=6').then(res => {
      const good = res.data.reviews.filter(r => r.rating >= 4);
      setReviews(good.slice(0, 6));
    }).catch(() => {});
    api.get('/reviews/stats').then(res => setReviewStats(res.data)).catch(() => {});
  }, []);

  // Check availability when date or time changes
  useEffect(() => {
    if (!resvForm.date || !resvForm.heure) { setAvailability(null); return; }
    const timer = setTimeout(async () => {
      setCheckingAvail(true);
      try {
        const res = await api.get(`/reservations/availability?date=${resvForm.date}&heure=${resvForm.heure}`);
        setAvailability(res.data);
      } catch { setAvailability(null); }
      finally { setCheckingAvail(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [resvForm.date, resvForm.heure]);

  const handleResvSubmit = async (e) => {
    e.preventDefault();
    setResvError('');
    if (!resvForm.email || !resvForm.nom || !resvForm.prenom || !resvForm.date || !resvForm.heure) return;
    setResvLoading(true);
    try {
      await api.post('/reservations', resvForm);
      setResvSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de l\'envoi. Veuillez reessayer.';
      setResvError(detail);
    } finally {
      setResvLoading(false);
    }
  };

  return (
    <div className="bg-[#fafaf9]">
      {/* Hero */}
      <section className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <img src={v.hero_image} alt="La Taverne" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-4 flex flex-col items-center gap-5 max-w-4xl mx-auto mt-16">
          <img src="/logo-taverne.png" alt="La Taverne" className="drop-shadow-2xl" style={{ width: `${v.logo_size}px`, maxWidth: '90vw' }} />
          <p className="text-stone-200 max-w-lg text-sm leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {v.hero_tagline}
          </p>
          <div className="flex gap-4 mt-4">
            <Link to={tableQR ? `/commande/${tableQR}` : '/menu'} data-testid="hero-cta-menu"
              className="bg-red-900 text-white px-8 py-3.5 text-[11px] uppercase tracking-[0.15em] hover:bg-red-950 transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              Notre Carte
            </Link>
            {!tableQR && (
              <Link to="/contact" data-testid="hero-cta-contact"
                className="border border-white/40 text-white px-8 py-3.5 text-[11px] uppercase tracking-[0.15em] hover:bg-white/10 transition-colors"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
                Nous Trouver
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="about-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-stone-400 block mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Notre Histoire</span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {v.about_title}
            </h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.about_text1}</p>
            <p className="text-stone-600 text-sm leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.about_text2}</p>
            <p className="text-stone-500 text-sm leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.about_text3}</p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-3">
            <img src={v.about_image1} alt="Interior" className="w-full h-64 md:h-80 object-cover col-span-2" />
            <img src={v.about_image2} alt="Plats" className="w-full h-40 md:h-52 object-cover" />
            <img src={v.about_image3} alt="Restaurant" className="w-full h-40 md:h-52 object-cover" />
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="py-20 px-6 md:px-12 max-w-7xl mx-auto" data-testid="gallery-section">
          <div className="text-center mb-12">
            <span className="text-[10px] tracking-[0.3em] uppercase text-stone-400 block mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Galerie</span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Notre Restaurant
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gallery.map((img, i) => (
              <div key={i} className="overflow-hidden group">
                <img src={img.url} alt={img.caption || `Photo ${i + 1}`}
                  className="w-full h-48 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
                {img.caption && <p className="text-xs text-center text-stone-400 mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{img.caption}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <section className="py-20 px-6 md:px-12 max-w-5xl mx-auto" data-testid="reviews-section">
          <div className="text-center mb-12">
            <span className="text-[10px] tracking-[0.3em] uppercase text-stone-400 block mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Temoignages</span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Avis de nos clients
            </h2>
            {reviewStats.total > 0 && (
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={20} className={s <= Math.round(reviewStats.average) ? 'text-amber-400 fill-amber-400' : 'text-stone-300'} />
                  ))}
                </div>
                <span className="text-stone-600 text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{reviewStats.average}/5</span>
                <span className="text-stone-400 text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>({reviewStats.total} avis)</span>
              </div>
            )}
          </div>

          {reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              {reviews.map((r, i) => (
                <div key={i} className="border border-stone-200 p-6 bg-white" data-testid={`home-review-${i}`}>
                  <div className="flex gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={14} className={s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'} />
                    ))}
                  </div>
                  {r.comment && (
                    <p className="text-stone-600 text-sm leading-relaxed mb-4 line-clamp-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      "{r.comment}"
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 text-xs font-bold">
                      {(r.name || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-stone-700" style={{ fontFamily: "'Outfit', sans-serif" }}>{r.name || 'Client'}</p>
                      {r.table_number && <p className="text-[10px] text-stone-400">Table {r.table_number}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center mb-10">
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={28} className="text-stone-200" />
                ))}
              </div>
              <p className="text-stone-500 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Soyez le premier a partager votre experience !
              </p>
            </div>
          )}

          <div className="text-center">
            <Link to="/avis" data-testid="leave-review-btn"
              className="bg-[#2c1810] text-white px-8 py-3.5 text-[11px] uppercase tracking-[0.15em] hover:bg-[#3d2817] transition-colors inline-block"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              Laisser un avis
            </Link>
          </div>
        </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <img src={v.cta_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-4">
          <h2 className="text-white text-3xl md:text-5xl font-light tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {v.cta_title}
          </h2>
          <p className="text-stone-300 text-sm mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.cta_subtitle}</p>
          <a href={`tel:${v.phone.replace(/\s/g, '')}`}
            className="bg-red-900 text-white px-10 py-4 text-[11px] uppercase tracking-[0.15em] hover:bg-red-950 transition-colors inline-block"
            style={{ fontFamily: "'Outfit', sans-serif" }}>
            {v.phone}
          </a>
          <div className="mt-5">
            <button onClick={() => { setShowReservation(true); setResvSuccess(false); }}
              className="border border-white/50 text-white px-10 py-4 text-[11px] uppercase tracking-[0.15em] hover:bg-white/10 transition-colors inline-block cursor-pointer"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="open-reservation-btn">
              Reserver en ligne
            </button>
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      {showReservation && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="reservation-modal">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-[#8B0000] p-5 flex justify-between items-center">
              <h3 className="text-white text-lg font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Reserver une table</h3>
              <button onClick={() => setShowReservation(false)} className="text-white/70 hover:text-white" data-testid="close-reservation-btn">
                <X size={22} />
              </button>
            </div>
            {resvSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 text-3xl">&#10003;</span>
                </div>
                <h4 className="text-xl font-semibold text-stone-800 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Demande envoyee !</h4>
                <p className="text-stone-600 text-sm mb-6">Vous recevrez un email de confirmation sous peu.</p>
                <button onClick={() => setShowReservation(false)}
                  className="bg-[#8B0000] text-white px-8 py-3 text-sm hover:bg-[#6B0000] transition-colors rounded-md"
                  data-testid="reservation-close-success">
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleResvSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Prenom *</label>
                    <input type="text" required value={resvForm.prenom} onChange={e => setResvForm(p => ({ ...p, prenom: e.target.value }))}
                      className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="Votre prenom" data-testid="resv-prenom" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Nom *</label>
                    <input type="text" required value={resvForm.nom} onChange={e => setResvForm(p => ({ ...p, nom: e.target.value }))}
                      className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="Votre nom" data-testid="resv-nom" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Telephone</label>
                  <input type="tel" value={resvForm.telephone} onChange={e => setResvForm(p => ({ ...p, telephone: e.target.value }))}
                    className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                    placeholder="0694 XX XX XX" data-testid="resv-telephone" />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Email * <span className="text-red-500">(obligatoire)</span></label>
                  <input type="email" required value={resvForm.email} onChange={e => setResvForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                    placeholder="votre@email.com" data-testid="resv-email" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Couverts *</label>
                    <input type="number" min="1" max="20" required value={resvForm.couverts} onChange={e => setResvForm(p => ({ ...p, couverts: parseInt(e.target.value) || 1 }))}
                      className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                      data-testid="resv-couverts" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Date *</label>
                    <input type="date" required value={resvForm.date} onChange={e => setResvForm(p => ({ ...p, date: e.target.value }))}
                      className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                      data-testid="resv-date" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Heure *</label>
                    <input type="time" required value={resvForm.heure} onChange={e => setResvForm(p => ({ ...p, heure: e.target.value }))}
                      className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                      data-testid="resv-heure" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Message (optionnel)</label>
                  <textarea value={resvForm.message} onChange={e => setResvForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B0000] transition-colors"
                    rows={2} placeholder="Allergies, occasion speciale..." data-testid="resv-message" />
                </div>
                {/* Availability indicator */}
                {resvForm.date && resvForm.heure && (
                  <div data-testid="availability-info">
                    {checkingAvail ? (
                      <p className="text-xs text-stone-400 text-center">Verification de la disponibilite...</p>
                    ) : availability ? (
                      <div className={`rounded-md p-3 text-center text-sm ${availability.couverts_disponibles > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        {availability.couverts_disponibles > 0 ? (
                          <p className="text-green-700">
                            Service {availability.service} : <strong>{availability.couverts_disponibles} couverts disponibles</strong> sur {availability.capacite_totale}
                          </p>
                        ) : (
                          <p className="text-red-700 font-medium">
                            Service {availability.service} complet pour cette date. Essayez un autre creneau.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
                {resvError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center" data-testid="resv-error">
                    <p className="text-red-700 text-sm">{resvError}</p>
                  </div>
                )}
                {/* Tolerance notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center" data-testid="tolerance-notice">
                  <p className="text-amber-800 text-xs font-medium">Une tolerance de 10 minutes maximum sera accordee apres l'heure de reservation. Passe ce delai, votre table pourra etre attribuee a un autre client.</p>
                </div>
                <button type="submit" disabled={resvLoading || (availability && availability.couverts_disponibles < resvForm.couverts)}
                  className="w-full bg-[#8B0000] text-white py-3.5 text-sm uppercase tracking-wider hover:bg-[#6B0000] transition-colors disabled:opacity-50 rounded-md font-medium"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="resv-submit-btn">
                  {resvLoading ? 'Envoi en cours...' : (availability && availability.couverts_disponibles < resvForm.couverts) ? 'Complet pour ce creneau' : 'Envoyer la demande'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Hours */}
      <section className="py-20 px-6 md:px-12 max-w-3xl mx-auto text-center" data-testid="hours-section">
        <span className="text-[10px] tracking-[0.3em] uppercase text-stone-400 block mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Horaires</span>
        <h2 className="text-3xl font-light tracking-tight text-stone-900 mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Quand nous retrouver
        </h2>
        <div className="border border-stone-200 p-8">
          <p className="text-stone-700 text-sm mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.hours_label}</p>
          <p className="text-stone-900 text-2xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{v.hours_text}</p>
          <p className="text-stone-400 text-xs mt-4 uppercase tracking-[0.2em]" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.hours_closed}</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
